import {
    DateInput,
    IconButton,
    Input,
    PageHeader
} from '@components/2-molecules';

import React, { FC, useEffect, } from 'react';

import {
    Card,
    Modal,
    PDFViewer,
    Row,
    Spacer,
    Title,
} from '@components/1-atoms';

import {
    AppTemplate
} from '@components/4-templates';

import {
    Button,
} from '@components/2-molecules';

import { ContactInput, DocumentSummary, LineItems } from '@components/3-organisms';
import { LineItemType } from '@components/3-organisms/LineItems';

import { ForwardedPageProps, STATE } from '@lib/constants';

import { useBusiness, useBusinessCountryCurrency } from '@lib/hooks/useBusiness';
import { useContact } from '@lib/hooks/useContact';
import { useDocumentEstimate } from '@lib/hooks/useDocument';
import { useProductsServices } from '@lib/hooks/useProductService';

import generateDocumentTemplateFromData from './DocumentView/documentTemplate';
import * as documentUtils from './DocumentView/utils';

import Sandwich from '@assets/icons/sandwich';

import * as utils from '@lib/utils';
import { withForwardedNavigationParams } from 'react-navigation-props-mapper';

export type PartialDocDataType = {
    subTotal: number;
    totalDiscount: number;
    totalBeforeTaxes: number;
    totalTaxes: number;
    grandTotal: number;
};


const EstimateCreate: FC<ForwardedPageProps> = ({ navigation, data = {}, onDone, ...props }) => {

    const [estimate, setEstimate] = useDocumentEstimate({ data: data, operation: 'POST', state: STATE.CANCEL });

    const [productsServices] = useProductsServices({ state: STATE.READY });

    // Identify the default price from the 'sellingPrice' or 'purchasingPrice' prop
    const defaultPriceKey = 'sellingPrice';

    const [business] = useBusiness();
    const [currency] = useBusinessCountryCurrency();
    const [contacts] = useContact();

    // Update estimate date
    useEffect(() => {
        const today = Date.now();
        const date = estimate?.data?.date || today;
        const dueDate = estimate?.data?.dueDate || today;
        updateData({ createdAt: date, dueDate: dueDate });
    }, []);

    // Prefill estimate number/name 
    useEffect(() => {
        // If there is business tracking the nextEstNumber defined
        if (business?.data?.nextEstNumber) {
            const nextEstNumber = utils.zeroPad(business?.data?.nextEstNumber, 3);
            const name = `EST-${nextEstNumber}`;
            updateData({ name: name });
        }
    }, [business]);

    const updateData = (data: any): void => setEstimate((estimate: any) => ({ ...estimate, data: { ...estimate.data, ...data } }));

    const createEstimate = (): void => setEstimate({ ...estimate, state: STATE.READY, message: 'Creating estimate ...' });

    const _onDone = (data: any) => navigation.navigate('DocumentView', { data: data });
    onDone = onDone || _onDone;
    utils.onStateChange(estimate, STATE.DONE, onDone);

    const [pdfUri, setPdfUri] = React.useState<null | string>();

    const [isPreviewModalVisible, setPreviewModalVisiblility] = React.useState(false);
    const _openPreviewModal = () => setPreviewModalVisiblility(true);
    const _closePreviewModal = () => setPreviewModalVisiblility(false);

    React.useEffect(() => {
        (async () => {
            const documentDef = generateDocumentTemplateFromData({ ...estimate?.data, business: business?.data }, currency?.data?.symbol);
            // TODO: This line freezes the page
            const base64 = await documentUtils.generatePdfDataURI(documentDef) as string;
            // // 📓: The PDF data URI is used to display A4 letter format of the document
            setPdfUri(base64);
        })();
    }, [estimate?.data, business?.data, currency?.data?.symbol]);

    // Update /calcultate the summary of the document / line-items
    React.useEffect(() => {
        const summary = {
            subTotal: 0,
            totalDiscount: 0,
            totalBeforeTaxes: 0,
            totalTaxes: 0,
            grandTotal: 0
        };

        estimate?.data?.lineItems?.forEach((lineItem: LineItemType,) => {
            summary.subTotal += lineItem.subTotal;
            summary.totalDiscount += lineItem.discount;
            summary.totalBeforeTaxes += lineItem.totalBeforeTaxes;
            summary.totalTaxes += lineItem.totalTaxes;
            summary.grandTotal += lineItem.total;
        });

        updateData(summary);
    }, [estimate?.data?.lineItems]);


    // TODO: CREATE NEW CONTACT OR PRODUCT/SERVICE AND REDIRECT BACK TO THE ESTIMATE CREATE PAGE WHILE PRESERVING THE DATA
    /*
        // TODO ✅: These implementations are for version 1.2.x. The code / app can be shipped to production WITHOUT
        // these implementation to any version prior 1.2.x.
        // 
        // ☐ Select newly created contact, and automatically redirect the user to the EstimateCreate page while
        // preserving any existing data of the estimate document.
        //
        // ☐ Add newly created product or service, and automatically redirect the user to the EstimateCreate page
        // while preserving any existing data of the estimate document.
        //
        // HOW TO DO 📚:
        // Display the page itself inside the modal. If the user chooses to use the drawer and go to another page,
        // cancel the operation, the EstimateCreate page along with all its modals will be unmounted.
        //
        // The navigation prop would not be provided to the ContactInput and LineItem components, the create pages
        // should be opened inside a modal.
        //
        // The modal should have no close button (as this creates another header). Instead, the page header back button
        // should close the modal, and the page header sandwich button should rely on the useNavigation hook to be functional.
        // The drawer z-index should be higher than all the modals.
        //
        // --We are doing this because upon back going to another page, the EstimateCreate, all the components(data) are
        // reset / unmounted.
        //
        // --Test this solution first using the Contact Create page.
    */

    return (
        <AppTemplate hooks={[estimate, productsServices, contacts,]}>



            <Modal visible={isPreviewModalVisible} onClose={_closePreviewModal} >
                {pdfUri ? <PDFViewer uri={pdfUri} /> : null}
            </Modal>

            <PageHeader
                title={estimate?.data?.name}
                left={<IconButton icon={<Sandwich />} onPress={navigation?.toggleDrawer} />} />

            <Card title='ESTIMATE' noRadius>
                {/* DOC HEADER: doc. number, date, expiration date */}
                <Row>
                    <Title large bold>{'HEADER'}</Title>
                </Row>
                <Row>
                    <Input
                        label="Number"
                        opaque={true}
                        border={false}
                        padding={false}
                        defaultValue={estimate?.data?.name}
                        onChange={(value) => updateData({ name: value })}
                    />
                </Row>
                <Spacer h={1} />
                <Row>
                    <DateInput
                        label="Date"
                        mini={true}
                        opaque={true}
                        border={false}
                        padding={false}
                        dateFormat="LLL dd, yy"
                        defaultValue={estimate?.data?.createdAt}
                        onChange={(value: Date) => updateData({ createdAt: value })}
                    />
                    <DateInput
                        label="Expiration date"
                        mini={true}
                        opaque={true}
                        border={false}
                        padding={false}
                        dateFormat="LLL dd, yy"
                        defaultValue={estimate?.data?.dueDate}
                        onChange={(value: Date) => updateData({ dueDate: value })}
                    />
                </Row>
                <Spacer h={2} />

                {/* Document receipient */}
                <ContactInput
                    label='Select the customer'
                    navigation={navigation}
                    defaultValue={estimate?.data?.contact}
                    data={contacts?.data}
                    onChange={(contact: any) => updateData({ contact: contact, contactId: contact?.id })}
                />
                <Spacer h={2} />

                {/* Document line items */}
                <LineItems
                    navigation={navigation}
                    defaultValue={estimate?.data?.lineItems}
                    data={productsServices?.data}
                    defaultPriceKey={defaultPriceKey}
                    currency={currency?.data?.symbol}
                    onChange={(value) => { updateData({ lineItems: value }) }}
                />
                <Spacer h={2} />

                {/* Document summary */}
                <DocumentSummary
                    currency={currency?.data?.symbol}
                    data={estimate?.data}
                />

                {/* Document footnote */}
                <Row><Title bold>{'NOTE TO CUSTOMER'}</Title></Row>
                <Input
                    multiline
                    defaultValue={estimate?.data?.noteToReceiver}
                    placeholder={'The note will appear on your estimate document footnote'}
                    onChange={(value: string) => { updateData({ noteToReceiver: value }) }} />
            </Card>
            <Spacer h={2} />

            <Button outlined onPress={_openPreviewModal}>PREVIEW IN PDF FORMAT</Button>
            <Spacer h={1} />

            <Button onPress={createEstimate}>CREATE ESTIMATE</Button>
            <Spacer h={2} />

        </AppTemplate>
    );
};

export default withForwardedNavigationParams<ForwardedPageProps>()(EstimateCreate);